import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI } from '../services/api';

const GROUPS = [
  { title: '👕 Everyday & work', match: (n) => /shirt|trouser|skirt|jumper|sweater|tie|polo/i.test(n) },
  { title: '🤵 Suits & jackets', match: (n) => /suit|blazer|jacket/i.test(n) },
  { title: '👗 Dresses & coats', match: (n) => /dress|coat/i.test(n) },
  { title: '🛏️ Bedding & home', match: (n) => /duvet|bedding|linen|curtain|blanket/i.test(n) }
];
const groupOf = (name) => (GROUPS.find((g) => g.match(name)) || { title: '✨ Other items' }).title;
const money = (n) => `£${Number(n).toFixed(2)}`;

const BookAlaCarte = () => {
  const [items, setItems] = useState([]);
  const [qty, setQty] = useState({});
  const [pickupDate, setPickupDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    ordersAPI.getPricing()
      .then((r) => setItems(r.data.filter((i) => i.active === undefined || i.active === 1)))
      .catch(() => setError('Failed to load items'));
  }, []);

  const setItemQty = (id, next) => setQty((q) => ({ ...q, [id]: Math.max(0, next) }));
  const selected = items.filter((i) => qty[i.id] > 0);
  const total = selected.reduce((s, i) => s + i.price * qty[i.id], 0);

  const minDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (selected.length === 0) return setError('Please add at least one item.');
    if (!pickupDate) return setError('Please choose a collection day.');
    setLoading(true);
    try {
      await ordersAPI.create({
        orderType: 'dry_cleaning',
        items: selected.map((i) => ({ id: i.id, quantity: qty[i.id] })),
        pickupDate,
        specialInstructions: notes
      });
      setSuccess(true);
      setTimeout(() => navigate('/my-orders'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0 && !error) return <div className="loading">Loading items…</div>;

  // group items, preserving group order
  const grouped = GROUPS.map((g) => g.title).concat('✨ Other items')
    .map((title) => ({ title, list: items.filter((i) => groupOf(i.item_name) === title) }))
    .filter((g) => g.list.length > 0);

  return (
    <div className="container">
      <h1>Book dry cleaning</h1>
      <p className="section-sub" style={{ textAlign: 'left', margin: '0.3rem 0 0' }}>
        Add your items, choose a collection day, and we'll take it from there.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="booking-layout">
          {/* Items */}
          <div>
            {grouped.map((group) => (
              <div className="card" key={group.title} style={{ marginBottom: '1.2rem' }}>
                <h3 style={{ marginBottom: '0.5rem' }}>{group.title}</h3>
                {group.list.map((item) => (
                  <div className="item-row" key={item.id}>
                    <div>
                      <strong>{item.item_name}</strong>
                      {item.description && (
                        <div style={{ fontSize: '0.85rem', color: 'var(--ink-soft)' }}>{item.description}</div>
                      )}
                    </div>
                    <span className="price-small">{money(item.price)}</span>
                    <div className="qty-stepper">
                      <button type="button" onClick={() => setItemQty(item.id, (qty[item.id] || 0) - 1)} disabled={!qty[item.id]} aria-label="Remove one">−</button>
                      <span className="qty">{qty[item.id] || 0}</span>
                      <button type="button" onClick={() => setItemQty(item.id, (qty[item.id] || 0) + 1)} aria-label="Add one">+</button>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            <div className="card">
              <div className="form-group">
                <label>Collection day</label>
                <input type="date" value={pickupDate} min={minDate()} onChange={(e) => setPickupDate(e.target.value)} required />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Anything we should know? (optional)</label>
                <textarea rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. delicate fabric, stain on the collar, buzzer is broken…" />
              </div>
            </div>
          </div>

          {/* Sticky summary */}
          <aside className="booking-summary">
            <div className="card">
              <h3>Your order</h3>
              {selected.length === 0 ? (
                <p style={{ color: 'var(--ink-soft)', margin: '0.6rem 0 0' }}>No items yet — add some from the left.</p>
              ) : (
                <div style={{ marginTop: '0.8rem' }}>
                  {selected.map((i) => (
                    <div className="summary-line" key={i.id}>
                      <span>{i.item_name} × {qty[i.id]}</span>
                      <span>{money(i.price * qty[i.id])}</span>
                    </div>
                  ))}
                  <div className="summary-total">
                    <span>Total</span>
                    <span className="price">{money(total)}</span>
                  </div>
                </div>
              )}

              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">Order placed — see you soon! Redirecting…</div>}

              <button type="submit" className="btn btn-accent btn-block" style={{ marginTop: '1rem' }} disabled={loading || selected.length === 0}>
                {loading ? 'Placing order…' : `Place order · ${money(total)}`}
              </button>
              <p style={{ fontSize: '0.82rem', color: 'var(--ink-soft)', textAlign: 'center', marginTop: '0.7rem' }}>
                🚐 Free collection &amp; delivery · ⏱️ Back within ~48 hours
              </p>
            </div>
          </aside>
        </div>
      </form>
    </div>
  );
};

export default BookAlaCarte;
